import { NavLink } from 'react-router-dom';

// P2b-3 §1.2: sticky tab strip on every rollout view (master + meta + google).
// Counts are gate-only (no filter-bar state) so the header is stable as
// users type into the filter bar. The master tab deliberately has no count —
// the row count for the master view already lives in the page header.
//
// The component is a dumb display: parents pass pre-computed counts. The
// active tab is also explicit — we don't infer from the URL because routing
// for nested grids may diverge from the prop in transient render states.

type Props = {
  rolloutId: string;
  active: 'master' | 'meta' | 'google';
  metaCount: number;
  googleCount: number;
};

export function RolloutTabStrip({ rolloutId, active, metaCount, googleCount }: Props) {
  return (
    <div className="sticky top-0 z-30 -mx-8 px-8 py-2 bg-white border-b border-slate-200">
      <nav
        className="flex items-center gap-1"
        role="tablist"
        aria-label="Rollout grids"
      >
        <Tab
          to={`/rollouts/${rolloutId}`}
          end
          isActive={active === 'master'}
          label="Master"
        />
        <Tab
          to={`/rollouts/${rolloutId}/grid/meta`}
          isActive={active === 'meta'}
          label="Meta"
          count={metaCount}
        />
        <Tab
          to={`/rollouts/${rolloutId}/grid/google`}
          isActive={active === 'google'}
          label="Google"
          count={googleCount}
        />
      </nav>
    </div>
  );
}

function Tab({
  to,
  isActive,
  label,
  count,
  end,
}: {
  to: string;
  isActive: boolean;
  label: string;
  count?: number;
  end?: boolean;
}) {
  // We compute `isActive` ourselves from the `active` prop rather than
  // using NavLink's automatic detection — see the component-level note —
  // but still use NavLink for accessibility-friendly link semantics.
  return (
    <NavLink
      to={to}
      end={end}
      role="tab"
      aria-selected={isActive}
      className={
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ' +
        (isActive
          ? 'bg-slate-900 text-white'
          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')
      }
    >
      <span>{label}</span>
      {count !== undefined && (
        <span
          className={
            'inline-flex items-center justify-center min-w-[1.5rem] px-1.5 rounded text-xs font-medium ' +
            (isActive ? 'bg-white/20' : 'bg-slate-200 text-slate-700')
          }
        >
          {count}
        </span>
      )}
    </NavLink>
  );
}
